import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import AddExperienceModal from '../Experience/AddExperienceModal';
import { useUserContext } from '../../contexts/UserContext';
import { getAEMContentFragments } from '../../utils/APIs';
import ErrorAlert from '../UI/Alert/ErrorAlert';
import { formatDate } from '../../utils/converter';
import { toast } from 'react-toastify';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import { getAgGridSortStorageKey } from '../UI/AgGrid';
import { Card, Button, Input, AgTableCard } from '../UI';

const ManageAEMContentFragments = () => {
    const { enums, content } = useContentfulContext();

    const [rowData, setRowData] = useState([]);
    const [showAddExperiencesModal, setShowAddExperiencesModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [gridApi, setGridApi] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    const { userData } = useUserContext();
    const navigator = useNavigate();

    useEffect(() => {
        if (userData.defaultAEMAdobeIO) {
            getActivityData();
        }
    }, [userData.defaultAEMAdobeIO, userData.defaultAEMFolder]);

    const getActivityData = async () => {
        try {
            if (!userData?.defaultAEMAdobeIO) {
                toast.error(content.LIST_AEM_FRAGMENTS_CONTENT.error.no_aem_found);
                return;
            }
            setLoading(true);
            const result = await getAEMContentFragments({
                token: userData?.token,
                organizationId: userData?.selectedOrganization?.org_token,
                aem_adobe_io_id: userData?.defaultAEMAdobeIO?._id,
                aem_folder_url: userData?.defaultAEMFolder?.href || "test url"
            });

            setError("");
            if (result.data?.entities?.length > 0) {
                setRowData(result.data.entities);
            } else {
                setRowData([]);
            }
        } catch (err) {
            setError(err?.message || content?.COMMON_MESSAGE.something_wrong_msg);
            toast.error(err?.message || content?.COMMON_MESSAGE.something_wrong_msg);
            navigator("/experience/manage-templates");
        } finally {
            setLoading(false);
        }
    };

    const onGridReady = (params) => {
        setGridApi(params.api);

        const storageKey = getAgGridSortStorageKey("manage-aem-content-fragments-table");
        const sortModel = JSON.parse(localStorage.getItem(storageKey));

        if (!sortModel || sortModel?.length === 0) {
            params.columnApi.applyColumnState({
                state: [
                    { colId: 'properties.modified', sort: 'desc' }
                ],
                applyOrder: false,
            });
        }
    };

    const autoSizeStrategy = useMemo(() => {
        return {
            type: "fitGridWidth",
            defaultMinWidth: 40,
            columnLimits: [
                {
                    colId: "properties.name",
                    minWidth: 260,
                },
                {
                    colId: "properties.createdBy",
                    minWidth: 200,
                },
                {
                    colId: "properties.modifiedBy",
                    minWidth: 200,
                },
                {
                    colId: "properties.created",
                    minWidth: 160,
                },
                {
                    colId: "properties.modified",
                    minWidth: 160,
                }
            ],
        };
    }, []);

    const handleCloseModal = () => {
        setShowAddExperiencesModal(false);
    };

    const onFilterTextBoxChanged = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        if (gridApi) {
            gridApi.setFilterModel({
                ...gridApi.getFilterModel(),
                "properties.name": value ? { type: 'contains', filter: value.toLowerCase() } : null
            });
        }
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        if (gridApi) {
            const currentFilter = gridApi.getFilterModel();
            delete currentFilter["properties.name"];
            gridApi.setFilterModel(currentFilter);
        }
    };

    const colDefs = [
        {
            headerName: content.LIST_AEM_FRAGMENTS_CONTENT.table_headers.title,
            field: "properties.name",
            cellRenderer: ({ data }) => (
                <Link
                    className="font-medium text-primary hover:text-primary-hover hover:underline"
                    to={`/experience/aem-content-fragments/${data.properties.name}`}
                >
                    {data.properties.name}
                </Link>
            ),
            filter: true,
            suppressMovable: true
        },
        {
            headerName: content.LIST_AEM_FRAGMENTS_CONTENT.table_headers.created_by,
            field: "properties.createdBy",
            filter: true,
            suppressMovable: true,
            cellRenderer: ({ value }) => (
                <span className="text-stride-body text-sm font-normal">
                    {value || '—'}
                </span>
            )
        },
        {
            headerName: content.LIST_AEM_FRAGMENTS_CONTENT.table_headers.last_modified_by,
            field: "properties.modifiedBy",
            filter: true,
            suppressMovable: true,
            cellRenderer: ({ value }) => (
                <span className="text-stride-body text-sm font-normal">
                    {value || '—'}
                </span>
            )
        },
        {
            headerName: content.LIST_AEM_FRAGMENTS_CONTENT.table_headers.created_date,
            field: "properties.created",
            cellRenderer: ({ data }) => (
                <span className="text-stride-muted text-xs">
                    {formatDate(data.properties.created)}
                </span>
            ),
            comparator: (date1, date2) => new Date(date1) - new Date(date2),
            suppressMovable: true,
            sortable: true
        },
        {
            headerName: content.LIST_AEM_FRAGMENTS_CONTENT.table_headers.last_modified_date,
            field: "properties.modified",
            cellRenderer: ({ data }) => (
                <span className="text-stride-muted text-xs">
                    {formatDate(data.properties.modified)}
                </span>
            ),
            comparator: (date1, date2) => new Date(date1) - new Date(date2),
            suppressMovable: true,
            sortable: true
        },
    ];

    return (
        <>
            {error && <ErrorAlert timeout={content.LIST_AEM_FRAGMENTS_CONTENT.timeout}>{error}</ErrorAlert>}
            <main id="main" className="stride-page-shell flex flex-col space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <span className="stride-eyebrow">Experiences / Manage</span>
                        <h1 className="text-2xl font-bold tracking-tight text-stride-heading">
                            {content.LIST_AEM_FRAGMENTS_CONTENT.title}
                        </h1>
                        <p className="text-sm text-stride-muted mt-1">
                            {rowData?.length || 0} total AEM content fragments available
                        </p>
                    </div>
                </div>

                {/* Empty State: No Adobe IO */}
                {!userData.defaultAdobeIO && (
                    <Card className="text-center p-12 max-w-lg mx-auto">
                        <p className="text-base text-stride-heading font-medium mb-4">
                            {content.LIST_AEM_FRAGMENTS_CONTENT.no_adobe_io}
                        </p>
                        <Button
                            variant="primary"
                            onClick={() => navigator('/setup/new-integration')}
                        >
                            {content.LIST_AEM_FRAGMENTS_CONTENT.create_new_adobe_io}
                        </Button>
                    </Card>
                )}

                <AddExperienceModal
                    showAddExperiencesModal={showAddExperiencesModal}
                    handleCloseModal={handleCloseModal}
                />

                {/* Quartz AG-Grid Table inside Card with Search in Header */}
                {userData.defaultAdobeIO && (
                    <AgTableCard
                        header={
                            <div className="w-full md:w-80">
                                <Input
                                    placeholder="Search by name..."
                                    value={searchTerm}
                                    onChange={onFilterTextBoxChanged}
                                    prefixIcon={<Search className="w-4 h-4 text-stride-muted" />}
                                    clearable
                                    onClear={handleClearSearch}
                                />
                            </div>
                        }
                        tableId="manage-aem-content-fragments-table"
                        loading={loading}
                        rowData={rowData}
                        columnDefs={colDefs}
                        autoSizeStrategy={autoSizeStrategy}
                        onGridReady={onGridReady}
                        rowSelection="multiple"
                        suppressRowClickSelection
                        pagination={true}
                        paginationPageSize={content.LIST_AEM_FRAGMENTS_CONTENT.default_page_size}
                        paginationPageSizeSelector={enums?.PAGINATION_PAGE_SIZE_SELECTOR}
                    />
                )}
            </main>
        </>
    );
};

export default ManageAEMContentFragments;
